import React, { useState, useEffect, useCallback } from 'react';
import { fetchInventoryBags, fetchLowStockAlerts, fetchInventoryUsageLogs, inventoryApiService } from '../../api';
import MonthlyInventoryAssignment from './MonthlyInventoryAssignment';
import ServiceConsumableMapping from './ServiceConsumableMapping';
import RefillManagement from './RefillManagement';
import LowStockAlertsPage from './LowStockAlertsPage';
import InventoryAnalytics from './InventoryAnalytics';
import InventoryTransferPage from './InventoryTransferPage';
import TransferInventoryModal from './TransferInventoryModal';
// import MonthlyVerification from './MonthlyVerification';  
import BagStockTransfer    from './BagStockTransfer';
import PurchaseBatchManagement from './Invoice/PurchaseBatchManagement';
import InventoryInvoices from './Invoice/InventoryInvoices';

// ─────────────────────────────────────────────
// DESIGN SYSTEM – mirrors Dashboard.js exactly
// ─────────────────────────────────────────────
const COLORS = {
  aquaBlue: '#11a8a4',
  grassGreen: '#7ed957',
  darkGrey: '#3c3b3f',
  vividPurple: '#9440dd',
  white: '#ffffff',
  lightGrey: '#f3f4f6',
  mediumGrey: '#e5e7eb',
  darkText: '#1f2937',
  mediumText: '#6b7280',
  lightText: '#9ca3af',
};

/**
 * InventoryDashboard Component
 *
 * Entry point for all inventory management.
 * Tab 1 – Dashboard: summary cards, active bags, low-stock alerts, recent activity.
 * Tab 2 – Monthly Assignment: create & dispatch monthly inventory bags.
 */
const InventoryDashboard = () => {
  // ── active tab ──
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard' | 'monthly-assignment'

  // ── inventory bags ──
  const [bags, setBags] = useState([]);
  const [bagsLoading, setBagsLoading] = useState(true);
  const [bagsError, setBagsError] = useState(null);

  // ── low stock alerts ──
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [alertsError, setAlertsError] = useState(null);

  // ── usage logs ──
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsError, setLogsError] = useState(null);
  const [transferModalBag, setTransferModalBag] = useState(null);

  const getItemCategory = useCallback((item) => {
    if (item.category) return item.category.toLowerCase();
    if (item.is_capital === true) return 'capital';
    if (item.item_type === 'capital') return 'capital';
    if (item.type === 'capital') return 'capital';
    return 'consumable';
  }, []);

  const fetchBags = useCallback(async () => {
    setBagsLoading(true);
    setBagsError(null);
    try {
      const data = await fetchInventoryBags();
      setBags(data);
      console.log('Fetched bags:', data);
    } catch (err) {
      console.error('Error fetching bags:', err);
      setBagsError(err.response?.data?.message || err.message || 'Failed to load inventory bags');
    } finally {
      setBagsLoading(false);
    }
  }, []);

  const fetchAlerts = useCallback(async () => {
    setAlertsLoading(true);
    setAlertsError(null);
    try {
      const data = await fetchLowStockAlerts();
      setAlerts(data);
    } catch (err) {
      console.error('Error fetching alerts:', err);
      setAlertsError(err.response?.data?.message || err.message || 'Failed to load alerts');
    } finally {
      setAlertsLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    setLogsError(null);
    try {
      const data = await fetchInventoryUsageLogs();
      setLogs(data);
    } catch (err) {
      console.error('Error fetching usage logs:', err);
      setLogsError(err.response?.data?.message || err.message || 'Failed to load activity');
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBags();
    fetchAlerts();
    fetchLogs();
  }, [fetchBags, fetchAlerts, fetchLogs]);

  // ── derived stats ──
  const totalConsumables = bags.reduce(
    (acc, bag) =>
      acc + (bag.inventory_items?.filter((i) => i.inventory_item?.category === 'CONSUMABLE').length || 0),
    0
  );
  const totalCapitalItems = bags.reduce(
    (acc, bag) =>
      acc + (bag.inventory_items?.filter((i) => i.inventory_item?.category === 'CAPITAL').length || 0),
    0
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'Invalid date';
    }
  };

  const getActionDetails = (actionType) => {
    const t = (actionType || '').toLowerCase();
    if (t.includes('deduction')) return { bg: '#fee2e2', text: '#dc2626', label: 'Deduction' };
    if (t.includes('transfer')) return { bg: '#dbeafe', text: '#1d4ed8', label: 'Transfer' };
    if (t.includes('refill')) return { bg: '#dcfce7', text: '#16a34a', label: 'Refill' };
    if (t.includes('manual')) return { bg: `${COLORS.vividPurple}20`, text: COLORS.vividPurple, label: 'Manual Usage' };
    return { bg: COLORS.lightGrey, text: COLORS.mediumText, label: actionType || 'Unknown' };
  };

  // ─────────────────────────────────────────────
  // SUMMARY CARD (inline-style, matches Dashboard.js card pattern)
  // ─────────────────────────────────────────────
  const SummaryCard = ({ title, value, icon, accentColor, loading, error }) => (
    <div
      style={{
        backgroundColor: COLORS.white,
        borderRadius: '8px',
        border: `1px solid ${COLORS.mediumGrey}`,
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)',
        padding: '20px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        overflow: 'hidden',
        flex: '1 1 180px',
        minWidth: '160px',
      }}
    >
      <div>
        <p style={{ fontSize: '13px', fontWeight: '500', color: COLORS.mediumText, margin: '0 0 6px 0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          {title}
        </p>
        {loading ? (
          <div style={{ height: '28px', width: '60px', backgroundColor: COLORS.mediumGrey, borderRadius: '4px', animation: 'pulse 1.5s infinite' }} />
        ) : error ? (
          <p style={{ margin: 0, color: '#ef4444', fontSize: '13px' }}>Error</p>
        ) : (
          <p style={{ margin: 0, fontSize: '28px', fontWeight: '700', color: COLORS.darkText }}>{value}</p>
        )}
      </div>
      <div
        style={{
          width: '44px',
          height: '44px',
          borderRadius: '50%',
          backgroundColor: `${accentColor}20`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
    </div>
  );

  // ─────────────────────────────────────────────
  // TAB BUTTON
  // ─────────────────────────────────────────────
  const TabButton = ({ id, label, icon }) => {
    const isActive = activeTab === id;
    return (
      <button
        onClick={() => setActiveTab(id)}
        style={{
          padding: '10px 20px',
          border: 'none',
          borderBottom: isActive ? `3px solid ${COLORS.aquaBlue}` : '3px solid transparent',
          backgroundColor: isActive ? `${COLORS.aquaBlue}15`:"transparent",
          color: isActive ? COLORS.aquaBlue : COLORS.mediumText,
          fontWeight: isActive ? '600' : '500',
          fontSize: '14px',
          cursor: 'pointer',
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: "12px",
          width: "100%",
          padding: "14px 20px",
          transition: 'color 0.15s, border-color 0.15s',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => {
          if (!isActive) e.currentTarget.style.color = COLORS.darkText;
        }}
        onMouseLeave={(e) => {
          if (!isActive) e.currentTarget.style.color = COLORS.mediumText;
        }}
      >
        <span>{icon}</span>
        {label}
      </button>
    );
  };

  // ─────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────
  return (
      <div
    style={{
      display: "flex",
      minHeight: "100vh",
      fontFamily: "'Inter', sans-serif",
      background: "#f8fafc",
    }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
      `}</style>

{/* Sidebar */}
<div
  style={{
    width: "270px",
    background: "#ffffff",
    borderRight: `1px solid ${COLORS.mediumGrey}`,
    display: "flex",
    flexDirection: "column",
    padding: "20px 0",
    flexShrink: 0,
    height: "100vh",
    position: "sticky",
    top: 0,
    overflowY: "auto",
  }}
>
<h2 style={{ fontSize: '20px', fontWeight: 'bold', color: COLORS.darkText, margin: 0 }}>Inventory Management</h2>

    <TabButton id="dashboard" label="Dashboard" icon="📊" />
    <TabButton id="monthly-assignment" label="Monthly Assignment" icon="📦" />
    <TabButton id="service-mapping" label="Service Mapping" icon="🔗" />
    <TabButton id="refill-management" label="Refill Management" icon="🧴" />
    <TabButton id="low-stock-alerts" label="Low Stock Alerts" icon="⚠️" />
    <TabButton id="analytics" label="Inventory Analytics" icon="📈" />
    <TabButton id="transfer-management" label="Transfer Inventory" icon="🔄" />
    <TabButton id="bag-transfer" label="Bag Transfer" icon="🚚" />
    <TabButton id="purchase-batches" label="Purchase Batches" icon="📦" />
    <TabButton id="inventory-invoices" label="Inventory Invoices" icon="🧾" />
</div>

<div
  style={{
    flex: 1,
    padding: "30px",
    overflowY: "auto",
  }}
>

      {/* ── TAB: DASHBOARD ── */}
      {activeTab === 'dashboard' && (
        <>
          {/* Header + Refresh */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '24px',
              flexWrap: 'wrap',
              gap: '12px',
            }}
          >
            <div>
              <h2 style={{ margin: '0 0 4px 0', fontSize: '22px', fontWeight: '700', color: COLORS.darkText }}>
                Inventory Dashboard
              </h2>
              <p style={{ margin: 0, color: COLORS.mediumText, fontSize: '14px' }}>
                Manage and monitor your medical inventory
              </p>
            </div>
            <button
              onClick={() => { fetchBags(); fetchAlerts(); fetchLogs(); }}
              style={{
                backgroundColor: COLORS.aquaBlue,
                color: COLORS.white,
                border: 'none',
                borderRadius: '6px',
                padding: '9px 16px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#0d8f8b')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = COLORS.aquaBlue)}
            >
              ↻ Refresh
            </button>
          </div>

          {/* Summary Cards */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '28px' }}>
            <SummaryCard title="Active Inventory Bags" value={bags.length} icon="📦" accentColor="#6366f1" loading={bagsLoading} error={bagsError} />
            <SummaryCard title="Low Stock Alerts" value={alerts.length} icon="⚠️" accentColor="#f59e0b" loading={alertsLoading} error={alertsError} />
            <SummaryCard title="Total Consumables" value={totalConsumables} icon="🧴" accentColor={COLORS.aquaBlue} loading={bagsLoading} error={bagsError} />
            <SummaryCard title="Capital Items" value={totalCapitalItems} icon="🔧" accentColor={COLORS.vividPurple} loading={bagsLoading} error={bagsError} />
          </div>

          {/* Two-column content area */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '20px', marginBottom: '24px' }}>

            {/* Active Inventory Bags */}
            <div
              style={{
                backgroundColor: COLORS.white,
                borderRadius: '8px',
                border: `1px solid ${COLORS.mediumGrey}`,
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)',
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: '16px 20px', borderBottom: `1px solid ${COLORS.mediumGrey}`, backgroundColor: COLORS.lightGrey, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ width: '4px', height: '18px', backgroundColor: '#6366f1', borderRadius: '2px', display: 'inline-block' }} />
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: COLORS.darkText }}>Active Inventory Bags</h3>
              </div>
              <div style={{ padding: '16px' }}>
                {bagsLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 0' }}>
                    <div style={{ border: '4px solid #e5e7eb', borderTop: `4px solid ${COLORS.aquaBlue}`, borderRadius: '50%', width: '28px', height: '28px', animation: 'spin 1s linear infinite' }} />
                  </div>
                ) : bagsError ? (
                  <div style={{ textAlign: 'center', padding: '24px 0' }}>
                    <p style={{ color: '#ef4444', marginBottom: '8px', fontSize: '14px' }}>{bagsError}</p>
                    <button onClick={fetchBags} style={{ color: COLORS.aquaBlue, background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px' }}>Retry</button>
                  </div>
                ) : bags.length === 0 ? (
                  <p style={{ textAlign: 'center', color: COLORS.mediumText, padding: '24px 0', margin: 0, fontSize: '14px' }}>No active bags found.</p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr style={{ backgroundColor: COLORS.lightGrey }}>
                          {['Bag ID', 'Technician', 'Period', 'Items', 'Status', 'Actions'].map((h) => (
                            <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: COLORS.mediumText, fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {bags.slice(0, 10).map((bag) => (
                          <tr key={bag.id} style={{ borderTop: `1px solid ${COLORS.lightGrey}` }}>
                            <td style={{ padding: '9px 10px', color: COLORS.darkText, fontWeight: '500' }}>BAG-{bag.id}</td>
                            <td style={{ padding: '9px 10px', color: COLORS.darkText }}>{bag.current_technician_details?.name || 'N/A'}</td>
                            <td style={{ padding: '9px 10px', color: COLORS.mediumText }}>{bag.month}/{bag.year}</td>
                            <td style={{ padding: '9px 10px', color: COLORS.mediumText }}>{bag.inventory_items?.length || 0}</td>
                            <td style={{ padding: '9px 10px' }}>
                              <span style={{ backgroundColor: '#dcfce7', color: '#16a34a', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                                {bag.status}
                              </span>
                            </td>
                            
                           <td style={{ padding: '9px 10px' }}>
                             <button
                               onClick={() => setTransferModalBag(bag)}
                               style={{
                                 backgroundColor: '#dc2626',
                                 color: '#ffffff',
                                 border: 'none',
                                 borderRadius: '5px',
                                 padding: '4px 10px',
                                 fontSize: '11px',
                                 fontWeight: '600',
                                 cursor: 'pointer',
                               }}
                               onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#b91c1c')}
                               onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#dc2626')}
                             >
                               🔄 Transfer
                             </button>
                           </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {bags.length > 10 && (
                      <p style={{ textAlign: 'center', color: COLORS.mediumText, fontSize: '12px', marginTop: '10px' }}>
                        Showing first 10 of {bags.length} bags
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Low Stock Alerts */}
            <div
              style={{
                backgroundColor: COLORS.white,
                borderRadius: '8px',
                border: `1px solid ${COLORS.mediumGrey}`,
                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)',
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: '16px 20px', borderBottom: `1px solid ${COLORS.mediumGrey}`, backgroundColor: COLORS.lightGrey, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ width: '4px', height: '18px', backgroundColor: '#f59e0b', borderRadius: '2px', display: 'inline-block' }} />
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: COLORS.darkText }}>Low Stock Alerts</h3>
              </div>
              <div style={{ padding: '16px' }}>
                {alertsLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 0' }}>
                    <div style={{ border: '4px solid #e5e7eb', borderTop: '4px solid #f59e0b', borderRadius: '50%', width: '28px', height: '28px', animation: 'spin 1s linear infinite' }} />
                  </div>
                ) : alertsError ? (
                  <div style={{ textAlign: 'center', padding: '24px 0' }}>
                    <p style={{ color: '#ef4444', marginBottom: '8px', fontSize: '14px' }}>{alertsError}</p>
                    <button onClick={fetchAlerts} style={{ color: COLORS.aquaBlue, background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px' }}>Retry</button>
                  </div>
                ) : alerts.length === 0 ? (
                  <p style={{ textAlign: 'center', color: COLORS.mediumText, padding: '24px 0', margin: 0, fontSize: '14px' }}>
                    ✓ No low stock alerts at this time.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '340px', overflowY: 'auto' }}>
                    {alerts.map((alert) => (
                      <div
                        key={alert.id}
                        style={{
                          backgroundColor: '#fffbeb',
                          borderLeft: '4px solid #f59e0b',
                          borderRadius: '6px',
                          padding: '12px',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '6px' }}>
                          <div>
                            <p style={{ margin: '0 0 4px 0', fontWeight: '600', color: COLORS.darkText, fontSize: '14px' }}>
                              {alert.inventory_item?.item_name}
                            </p>
                            <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: COLORS.mediumText }}>
                              <span>Current: <strong style={{ color: '#d97706' }}>{alert.current_quantity || alert.quantity_remaining || 0}</strong></span>
                              <span>Threshold: <strong>{alert.threshold || alert.low_stock_threshold || 0}</strong></span>
                            </div>
                          </div>
                          <span style={{ backgroundColor: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                            ⚠️ Low Stock
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div
            style={{
              backgroundColor: COLORS.white,
              borderRadius: '8px',
              border: `1px solid ${COLORS.mediumGrey}`,
              boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)',
              overflow: 'hidden',
            }}
          >
            <div style={{ padding: '16px 20px', borderBottom: `1px solid ${COLORS.mediumGrey}`, backgroundColor: COLORS.lightGrey, display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '4px', height: '18px', backgroundColor: COLORS.grassGreen, borderRadius: '2px', display: 'inline-block' }} />
              <div>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: COLORS.darkText }}>Recent Inventory Activity</h3>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: COLORS.mediumText }}>Deductions, transfers, refills, and manual usage</p>
              </div>
            </div>
            <div style={{ padding: '16px' }}>
              {logsLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 0' }}>
                  <div style={{ border: `4px solid #e5e7eb`, borderTop: `4px solid ${COLORS.grassGreen}`, borderRadius: '50%', width: '28px', height: '28px', animation: 'spin 1s linear infinite' }} />
                </div>
              ) : logsError ? (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <p style={{ color: '#ef4444', marginBottom: '8px', fontSize: '14px' }}>{logsError}</p>
                  <button onClick={fetchLogs} style={{ color: COLORS.aquaBlue, background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px' }}>Retry</button>
                </div>
              ) : logs.length === 0 ? (
                <p style={{ textAlign: 'center', color: COLORS.mediumText, padding: '24px 0', margin: 0, fontSize: '14px' }}>
                  No recent inventory activity found.
                </p>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ backgroundColor: COLORS.lightGrey }}>
                        {['Action', 'Item', 'Qty', 'Technician', 'Camp', 'Usage Type', 'Date'].map((h) => (
                          <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: COLORS.mediumText, fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {logs.slice(0, 15).map((log) => {
                        const action = getActionDetails(log.action_type || log.action);
                        return (
                          <tr key={log.id} style={{ borderTop: `1px solid ${COLORS.lightGrey}` }}>
                            <td style={{ padding: '9px 10px' }}>
                              <span style={{ backgroundColor: action.bg, color: action.text, padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                                {action.label}
                              </span>
                            </td>
                            <td style={{ padding: '9px 10px', color: COLORS.darkText, fontWeight: '500' }}>
                              {log.inventory_item?.item_name || log.inventory_item_name || 'Unknown'}
                            </td>
                            <td style={{ padding: '9px 10px', color: '#dc2626', fontWeight: '600' }}>
                              -{log.quantity_used || 0}
                            </td>
                            <td style={{ padding: '9px 10px', color: COLORS.mediumText }}>
                              {log.technician?.user?.name || log.technician_name || 'System'}
                            </td>
                            <td style={{ padding: '9px 10px', color: COLORS.mediumText }}>
                              {log.camp?.camp_name || log.camp_name || 'N/A'}
                            </td>
                            <td style={{ padding: '9px 10px' }}>
                              <span style={{
                                backgroundColor: log.usage_type === 'AUTO' ? '#dbeafe' : `${COLORS.vividPurple}18`,
                                color: log.usage_type === 'AUTO' ? '#1d4ed8' : COLORS.vividPurple,
                                padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600'
                              }}>
                                {log.usage_type}
                              </span>
                            </td>
                            <td style={{ padding: '9px 10px', color: COLORS.mediumText }}>{formatDate(log.used_at)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {logs.length > 15 && (
                    <p style={{ textAlign: 'center', color: COLORS.mediumText, fontSize: '12px', marginTop: '10px' }}>
                      Showing last 15 activities
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── TAB: MONTHLY ASSIGNMENT ── */}
      {activeTab === 'monthly-assignment' && <MonthlyInventoryAssignment />}
      {activeTab === 'transfer-management' && <InventoryTransferPage />}
      {activeTab === 'service-mapping' && <ServiceConsumableMapping />}
      {activeTab === 'refill-management' && <RefillManagement />}
      {activeTab === 'low-stock-alerts'   && <LowStockAlertsPage />}
      {activeTab === 'analytics' && <InventoryAnalytics />}
      {/* {activeTab === 'monthly-verify' && <MonthlyVerification />} */}
      {activeTab === 'bag-transfer' && <BagStockTransfer />}
      {activeTab === 'purchase-batches' && <PurchaseBatchManagement />}
      {activeTab === 'inventory-invoices' && <InventoryInvoices />}
    
  
      {transferModalBag && (
        <TransferInventoryModal
          bag={transferModalBag}
          onClose={() => setTransferModalBag(null)}
          onSuccess={(updatedBag) => {
            setTransferModalBag(null);
            fetchBags();  // Refresh the bags list
            alert(`✅ BAG-${updatedBag.id} transferred to ${updatedBag.current_technician_details?.name || 'new technician'}.`);
          }}
        />
      )}
    </div>
    </div>
  );
};

export default InventoryDashboard;